import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { APIgetCollection } from '../../../../api/Collections';
import { APIgetPageCollectionFiles, APIgetPageCollections } from '../../../../api/Pages';
import { APIgetFiles } from '../../../../api/Files';

export const useLiveCollection = (
  selectedCollection,
  pageId,
  isEditing,
  allowedCollectionUuids = [],
) => {
  const [fetchedCollection, setFetchedCollection] = useState(null);
  const [loading, setLoading] = useState(false);
  const selectedRef = useRef(null);

  const selected = useMemo(() => {
    if (!selectedCollection) {
      return null;
    }

    return {
      ...selectedCollection,
      files: Array.isArray(selectedCollection.files) ? selectedCollection.files : null,
    };
  }, [selectedCollection]);

  const selectedIdentifier = selected?.uuid || selected?.id;
  const selectedUuid = selected?.uuid || '';
  const selectedFilesKey = useMemo(() => {
    if (!Array.isArray(selected?.files) || selected.files.length === 0) {
      return '';
    }

    return selected.files
      .map(file => (file?.uuid || file?.id || file?.path || ''))
      .join('|');
  }, [selected?.files]);

  const hasFiles = !!selectedFilesKey;
  const normalizedAllowedUuids = useMemo(
    () => Array.from(new Set(
      (Array.isArray(allowedCollectionUuids) ? allowedCollectionUuids : []).filter(Boolean),
    )),
    [allowedCollectionUuids],
  );
  const allowedSet = useMemo(
    () => new Set(normalizedAllowedUuids),
    [normalizedAllowedUuids],
  );
  const usePageEndpointsInEdit = isEditing && !!pageId && allowedSet.has(selectedUuid);

  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  useEffect(() => {
    const currentSelected = selectedRef.current;

    if (!currentSelected) {
      setFetchedCollection(null);
      setLoading(false);
      return;
    }

    if (hasFiles || !selectedIdentifier) {
      setFetchedCollection(currentSelected);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    let fetchCollection;
    if (usePageEndpointsInEdit && selectedUuid) {
      fetchCollection = Promise.all([
        APIgetPageCollections(pageId, [selectedUuid]),
        APIgetPageCollectionFiles(
          pageId,
          selectedUuid,
          { page: 1, per_page: 30 },
        ),
      ]).then(([collectionRes, filesRes]) => {
        const collectionMeta = collectionRes.data?.data?.[0]
          ?? collectionRes.data?.[0]
          ?? currentSelected;
        const files = filesRes.data?.data?.data
          ?? filesRes.data?.data
          ?? filesRes.data
          ?? [];
        return { ...collectionMeta, files };
      });
    } else if (isEditing && selectedUuid) {
      fetchCollection = APIgetFiles({
        collectionUuid: selectedUuid,
        page: 1,
        per_page: 30,
      }).then((response) => {
        const files = response.data.data;
        return { ...currentSelected, files };
      });
    } else if (isEditing) {
      fetchCollection = APIgetCollection(selectedIdentifier, { withFiles: true })
        .then((res) => (res.data || currentSelected));
    } else if (pageId && selectedUuid) {
      fetchCollection = Promise.all([
        APIgetPageCollections(pageId, [selectedUuid]),
        APIgetPageCollectionFiles(
          pageId,
          selectedUuid,
          { page: 1, per_page: 20 },
        ),
      ]).then(([collectionRes, filesRes]) => {
        const collectionMeta = collectionRes.data?.data?.[0]
          ?? collectionRes.data?.[0]
          ?? currentSelected;
        const files = filesRes.data?.data?.data
          ?? filesRes.data?.data
          ?? filesRes.data
          ?? [];
        return { ...collectionMeta, files };
      });
    } else {
      fetchCollection = APIgetCollection(selectedIdentifier, { withFiles: true })
        .then((res) => (res.data || currentSelected));
    }

    fetchCollection
      .then((item) => {
        if (cancelled) return;
        setFetchedCollection(item || currentSelected);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setFetchedCollection(currentSelected);
        setLoading(false);
      });

    // eslint-disable-next-line consistent-return
    return () => { cancelled = true; };
  }, [
    selectedIdentifier,
    selectedFilesKey,
    hasFiles,
    pageId,
    isEditing,
    selectedUuid,
    usePageEndpointsInEdit,
  ]);

  const liveCollection = useMemo(() => {
    if (!selected) return null;
    const live = fetchedCollection || selected;

    return {
      ...live,
      title: selected.title ?? live.title,
      subtitle: selected.subtitle ?? live.subtitle,
      custom_url: selected.custom_url ?? live.custom_url,
    };
  }, [fetchedCollection, selected]);

  return { liveCollection, loading };
};
